import { ReactNode, RefObject, useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import type { RowData } from '@tanstack/react-table';
import { useVirtualizer, VirtualItem, Virtualizer } from '@tanstack/react-virtual';
import type { DataTableRow } from '../dataTableFeatures';
import { dataTableRowLayoutHeight } from './dataTableRowLayoutHeight';

export interface IDataTableRowsProps<T extends RowData> {
    rows: DataTableRow<T>[];
    /** Виртуализация: в DOM живут только видимые строки плюс небольшой запас. */
    virtual: boolean;
    /** Скролл-контейнер, в котором меряются позиции строк. */
    scrollParent: HTMLElement | null;
    /** Смещение начала списка от верха скролл-контейнера (шапка, панели). */
    scrollMargin: number;
    /** Оценка высоты строки до измерения. */
    estimateSize: number;
    /** Строки одинаковой высоты (режим «1 строка»): измеряется только первая, её
     *  высота используется для всех. Иначе каждая строка меряется отдельно —
     *  наблюдатель размера на каждую строку заметно дороже при быстрой прокрутке. */
    uniformRows?: boolean;
    listRef: RefObject<HTMLUListElement | null>;
    renderRow: (row: DataTableRow<T>, index: number, vi?: VirtualItem, measure?: (el: HTMLElement | null) => void) => ReactNode;
    /** Отдаёт виртуализатор владельцу: sticky-заголовок группы читает позиции строк. */
    onVirtualizer?: (virtualizer: Virtualizer<HTMLElement, Element>) => void;
    /** Смена значения означает, что прежние высоты строк недействительны
     *  (переключили режим переноса, изменился состав колонок). */
    measureKey?: string;
    /** Строки, реально оказавшиеся в DOM (окно виртуализации или весь список).
     *  Владелец считает по ним обзоры, которым иначе пришлось бы сверять все
     *  строки набора: в форме это состав командной колонки и трек шеврона. */
    onRenderedRows?: (rows: T[]) => void;
}

/**
 * Список строк таблицы.
 *
 * Живёт отдельным компонентом ради прокрутки: состояние виртуализатора меняется
 * на каждый её шаг, и, будь оно в самой таблице, каждый кадр перерисовывал бы и
 * тулбар, и шапку с фильтрами, и пейджер — то есть всё, что от прокрутки не
 * зависит. Здесь перерисовываются только строки.
 */
function DataTableRows<T extends RowData>({
    rows,
    virtual,
    scrollParent,
    scrollMargin,
    estimateSize,
    listRef,
    renderRow,
    onVirtualizer,
    uniformRows = false,
    measureKey,
    onRenderedRows,
}: IDataTableRowsProps<T>) {
    const [uniformHeight, setUniformHeight] = useState<number | null>(null);
    const uniformHeightMeasuredRef = useRef(false);
    const measureFirst = useCallback((el: HTMLElement | null) => {
        if (!el || uniformHeightMeasuredRef.current) return;
        // offsetHeight не зависит от transform-анимации родительской модалки.
        // getBoundingClientRect во время появления модалки возвращает уменьшенную
        // высоту, из-за чего следующие строки перекрывают нижние разделители.
        const height = dataTableRowLayoutHeight(el);
        if (height <= 0) return;
        uniformHeightMeasuredRef.current = true;
        setUniformHeight(height);
    }, []);
    // Оценка высоты для ещё не измеренных строк: пока она расходится с фактической,
    // при быстрой прокрутке между строками мелькают провалы (строку ставят по
    // оценке и лишь потом меряют). Поэтому оценку уточняем средней по уже
    // измеренным строкам этого же списка.
    const [avgHeight, setAvgHeight] = useState<number | null>(null);
    const avgRef = useRef({ sum: 0, count: 0 });
    const sampledElementsRef = useRef(new WeakSet<HTMLElement>());
    const measuredNonVirtualRowsRef = useRef(new Map<string, number>());
    const rowHeight = uniformRows ? (uniformHeight ?? estimateSize) : (avgHeight ?? estimateSize);

    const virtualizer = useVirtualizer({
        // React 19 запрещает flushSync во время commit. Измерение строк идёт из
        // ref-колбэков именно в этой фазе, поэтому синхронный режим TanStack
        // засорял консоль предупреждениями при восстановлении большого списка.
        useFlushSync: false,
        enabled: virtual,
        count: rows.length,
        getScrollElement: () => scrollParent,
        // После раскрытия дерево может впервые превысить порог виртуализации.
        // При enabled=false TanStack сбрасывает offset; начальный ноль при
        // включении перезаписал бы уже прокрученное положение контейнера.
        // Функцией: TanStack читает её только при сброшенном offset. Чтение scrollTop в каждом рендере после
        // коммита, испачкавшего DOM (выбор строки), — лишний синхронный пересчёт раскладки всей страницы.
        initialOffset: () => scrollParent?.scrollTop ?? 0,
        estimateSize: (index) => measuredNonVirtualRowsRef.current.get(rows[index].id) ?? rowHeight,
        getItemKey: (i) => rows[i].id,
        // запас строк за краем: больше — плавнее при быстром скролле, но дороже
        // каждый кадр. Строки разной высоты меряются после отрисовки, поэтому им
        // запас нужнее, чем списку одинаковых строк
        overscan: uniformRows ? 4 : 8,
        scrollMargin,
    });
    // Смещение списка (scrollMargin) владелец меряет эффектом, и у таблицы на скрытой вкладке оно устаревает: при показе
    // строки меряются раньше перемера, и компенсация их размера по старому смещению прокручивала внешний контейнер
    // (карточка уезжала вниз при переключении вкладки). Пока смещение не сходится с фактическим — не компенсируем,
    // иначе — как по умолчанию в TanStack.
    virtualizer.shouldAdjustScrollPositionOnItemSizeChange = (item, _delta, instance) => {
        const list = listRef.current;
        const parent = instance.scrollElement;
        if (list && parent instanceof Element) {
            const actual = list.getBoundingClientRect().top - parent.getBoundingClientRect().top + parent.scrollTop;
            if (Math.abs(actual - instance.options.scrollMargin) > 1) return false;
        }
        const { enabled, initialOffset } = instance.options;
        const base = !enabled
            ? 0
            : (instance.scrollOffset ?? (typeof initialOffset === 'function' ? initialOffset() : initialOffset));
        const offset = base + instance.scrollAdjustments;
        return instance.itemSizeCache.has(item.key)
            ? item.end <= offset && instance.scrollDirection !== 'backward'
            : item.start < offset;
    };

    useEffect(() => {
        onVirtualizer?.(virtualizer);
    }, [onVirtualizer, virtualizer]);

    // Окно отрисованных строк — владельцу. Виртуализатор пересчитывает его на
    // каждый шаг прокрутки, поэтому отдаём набор, а не подписку на состояние:
    // получатель сравнивает свои выводы сам и лишних перерисовок не вызывает.
    // Состав окна считаем только для подписчика: без него это была бы лишняя
    // работа на каждый кадр прокрутки в каждой таблице приложения.
    const renderedIndexes = onRenderedRows && virtual ? virtualizer.getVirtualItems().map((vi) => vi.index) : null;
    const renderedKey = !onRenderedRows ? '' : renderedIndexes ? renderedIndexes.join(',') : `all:${rows.length}`;
    const renderedRef = useRef<{ indexes: number[] | null; rows: DataTableRow<T>[] }>({ indexes: null, rows });
    renderedRef.current = { indexes: renderedIndexes, rows };
    useEffect(() => {
        if (!onRenderedRows) return;
        const { indexes, rows: current } = renderedRef.current;
        const list = indexes ? indexes.map((index) => current[index]).filter(Boolean) : current;
        onRenderedRows(list.map((row) => row.original));
        // renderedKey описывает состав окна, rows — сами строки
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [onRenderedRows, renderedKey, rows]);

    /** Измерение строки переменной высоты + уточнение средней оценки. */
    const measureDynamic = useCallback(
        (el: HTMLElement | null) => {
            virtualizer.measureElement(el);
            if (!el || sampledElementsRef.current.has(el)) return;
            sampledElementsRef.current.add(el);
            const stats = avgRef.current;
            // хватает выборки первых экранов: дальше оценка только шумела бы,
            // а её смена пересчитывает позиции всего списка. Выходим ДО чтения
            // размера: getBoundingClientRect на свежесмонтированной строке — это
            // синхронный пересчёт раскладки всей таблицы (замер на таблице в 42
            // колонки: ~5 мс на строку), и на каждом шаге прокрутки он повторялся
            // бы для каждой новой строки уже без всякой пользы
            if (stats.count >= 60) return;
            const height = dataTableRowLayoutHeight(el);
            if (height <= 0) return;
            stats.sum += height;
            stats.count += 1;
            const next = stats.sum / stats.count;
            setAvgHeight((prev) => (prev != null && Math.abs(prev - next) < 2 ? prev : next));
        },
        [virtualizer],
    );

    // Высоты строк кешируются виртуализатором: после смены режима переноса или
    // ширин колонок они остаются от прежнего вида, и между строками появляются
    // провалы. Меряем заново — в том числе уже смонтированные строки: их
    // ref-колбэки повторно не зовутся, поэтому без явного measureElement список
    // стоял бы по старым высотам. Раньше это решал key={measureKey} на списке,
    // но пересоздание строк перемонтирует их DOM (мигают картинки в ячейках) и
    // стоит дорого при live-ресайзе колонок.
    //
    // ПОЛНЫЙ сброс (virtualizer.measure() всех строк + оценка высоты заново) —
    // только при смене режима переноса: там меняются высоты и невидимых строк.
    // Смена шаблона колонок (live-ресайз шлёт её на каждый сдвиг мыши) обходится
    // перемером смонтированных строк: полный сброс на каждый шаг откатывал
    // высоты за экраном к оценке (скроллбар и позиции дёргались, на слабых
    // машинах между строками мигали серые полосы) и пересчитывал измерения
    // всего списка. Невидимым строкам старая высота ближе оценки, а при
    // монтировании их всё равно перемерят ref-колбэки.
    const firstMeasureKey = useRef(measureKey);
    const measuredRowsRef = useRef(rows);
    const wrapModeOf = (key: string | undefined) => String(key ?? '').split('|')[0];
    // layout-эффект: перемер до отрисовки кадра, иначе при live-ресайзе колонок
    // wrap-таблицы каждый шаг показывал бы кадр со старыми позициями строк
    useLayoutEffect(() => {
        if (firstMeasureKey.current === measureKey) {
            measuredRowsRef.current = rows;
            return;
        }
        // Частичный перемер допустим только при ТЕХ ЖЕ строках (live-ресайз:
        // меняются лишь ширины). Смена состава строк вместе с шаблоном (включили
        // группировку — колонка ушла, строки другие) требует полного сброса,
        // иначе виртуализатор остаётся с измерениями прежнего списка и его окно
        // «примерзает» к началу.
        const modeChanged =
            wrapModeOf(firstMeasureKey.current) !== wrapModeOf(measureKey) || measuredRowsRef.current !== rows;
        firstMeasureKey.current = measureKey;
        measuredRowsRef.current = rows;
        measuredNonVirtualRowsRef.current.clear();
        if (modeChanged) {
            uniformHeightMeasuredRef.current = false;
            sampledElementsRef.current = new WeakSet<HTMLElement>();
            setUniformHeight(null);
            setAvgHeight(null);
            avgRef.current = { sum: 0, count: 0 };
            virtualizer.measure();
            // Пересчёт измерений по уже очищенному кэшу — обязателен перед
            // перемером: virtualizer пишет новую высоту строки, только если она
            // отличается от прежней, а прежние (по старому режиму) до пересчёта
            // ещё лежат в его буфере. Строка, чья высота в обоих режимах
            // одинакова, записи не получала и оставалась на средней оценке — под
            // такими строками висел зазор в несколько пикселей.
            virtualizer.getTotalSize();
        }
        const list = listRef.current;
        if (!list) return;
        if (uniformRows) {
            const first = list.querySelector<HTMLElement>('li');
            if (first) measureFirst(first);
            return;
        }
        list.querySelectorAll<HTMLElement>('li[data-index]').forEach((el) => {
            measureDynamic(el);
        });
    }, [measureKey, virtualizer, uniformRows, listRef, measureFirst, measureDynamic]);

    // При переходе обычного списка в виртуальный уже показанные строки имеют
    // известные высоты. Подмена их оценкой (56px) сдвигала раскрываемый узел,
    // даже когда начальный scrollTop был передан правильно. Сохраняем замеры
    // короткого списка; новые дети до отрисовки используют обычную оценку.
    useLayoutEffect(() => {
        if (virtual) return;
        const list = listRef.current;
        if (!list) return;
        const measure = () => {
            const heights = new Map<string, number>();
            for (const element of list.children) {
                if (!(element instanceof HTMLElement)) continue;
                const id = element.dataset.rowKey;
                const height = dataTableRowLayoutHeight(element);
                if (id && height > 0) heights.set(id, height);
            }
            measuredNonVirtualRowsRef.current = heights;
        };
        measure();
        const observer = new ResizeObserver(measure);
        observer.observe(list);
        return () => observer.disconnect();
    }, [virtual, rows, listRef, measureKey]);

    if (!virtual) {
        return (
            <div className="dtb__rowsclip">
                <ul ref={listRef} className="dtb__rows" role="rowgroup" data-testid="dtb-body">
                    {rows.map((row, i) => renderRow(row, i))}
                </ul>
            </div>
        );
    }

    return (
        <div className="dtb__rowsclip">
            <ul
                ref={listRef}
                className="dtb__rows dtb__rows--virtual"
                role="rowgroup"
                data-testid="dtb-body"
                style={{ position: 'relative', height: virtualizer.getTotalSize() }}
            >
                {virtualizer.getVirtualItems().map((vi) =>
                    renderRow(
                        rows[vi.index],
                        vi.index,
                        vi,
                        // в режиме одинаковых строк замер у всех строк один и тот же: меряет первая
                        // ОТРИСОВАННАЯ (после прокрутки строки с индексом 0 в DOM уже нет), остальные выходят
                        // сразу. Проп «только у первой» менялся бы у строк на краю окна на каждом шаге прокрутки
                        uniformRows ? measureFirst : measureDynamic,
                    ),
                )}
            </ul>
        </div>
    );
}

export default DataTableRows;
